import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { getLevelName, Size, useTeamCache, useUpdateCompany, useUpdateTeam } from '@front/model'
import { useToastError } from '@hooks'
import { Group, Stack, Text, TextInput } from '@mantine/core'
import { isEmpty, isNotEmpty, type CompanyData, type TeamData, type TeamUpdateData } from '@terros/common'
import { cn } from '@util'
import { ButtonCancel, ButtonDelete, ButtonEdit, ButtonSave, ConditionTooltip } from 'app/components'
import { AddTeamButton } from '../../addTeam'
import { useTeamDrag } from '../TeamDragContext'
import { useTeamHierarchyContext } from '../TeamHierarchyContext'
import { ButtonVisibility } from './ButtonVisibility'
import { TeamNode } from './node'

type LevelRowProps = {
  teams: TeamData[]
  level: number
}

export const LevelRow = ({ teams, level }: LevelRowProps): ReactElement => {
  const { company } = useTeamHierarchyContext()
  const { dragState, setDragOverLevel, canDrop, endDrag } = useTeamDrag()
  const isDragOver = dragState.dragOverLevel === level
  const isValidDropTarget = canDrop(null, level)
  const cache = useTeamCache()
  const { update } = useUpdateTeam()
  const onDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault()
      e.stopPropagation()
      if (isValidDropTarget) {
        setDragOverLevel(level)
      }
    },
    [level, isValidDropTarget, setDragOverLevel]
  )

  const onDragExit = useCallback((): void => {
    setDragOverLevel(null)
  }, [setDragOverLevel])

  const onDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
      e.preventDefault()
      e.stopPropagation()
      const draggedTeam = dragState.draggedTeam
      endDrag()
      if (isValidDropTarget && draggedTeam) {
        const updateData: TeamUpdateData = {
          teamId: draggedTeam.teamId,
          level,
          parentId: company.highestTeamLevel === level ? null : draggedTeam.parentId,
        }
        // optimistic update
        cache.partialUpdateTeam(updateData)
        update(updateData).catch(() => {
          cache.partialUpdateTeam(draggedTeam)
        })
      }
      endDrag()
    },
    [level, dragState.draggedTeam, isValidDropTarget, endDrag, update, cache, company]
  )

  const onDragEnd = useCallback((): void => {
    endDrag()
  }, [endDrag])

  return (
    <div
      className={cn(
        'flex min-h-[120px] gap-4 rounded-lg border border-transparent p-2xl',
        isDragOver && (isValidDropTarget ? 'border-emerald-filled' : 'border-rose-filled')
      )}
      onDragEnd={onDragEnd}
      onDragExit={onDragExit}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <RowLevelLabel level={level} />
      <div className='flex flex-row items-stretch justify-center gap-8'>
        {teams.map((team) => (
          <TeamNode key={team.teamId} team={team} />
        ))}
      </div>
    </div>
  )
}

const RowLevelLabel = ({ level }: { level: number }): ReactNode => {
  const {
    teams: allTeams,
    toggleTeamCollapse,
    collapsedTeams,
    withPermissions,
    canEdit,
    company,
    hideLevelLabels,
  } = useTeamHierarchyContext()
  const teamLevels = company?.levelNames
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [levelName, setLevelName] = useState(getLevelName(company, level))
  const inputRef = useRef<HTMLInputElement>(null)
  const { update, isUpdating, error } = useUpdateCompany()
  useToastError(error)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleEdit = (): void => {
    setIsEditing(true)
  }

  const handleCancel = (): void => {
    setLevelName(getLevelName(company, level))
    setIsEditing(false)
  }

  const handleSave = async (): Promise<void> => {
    if (!company) return
    setIsEditing(false)
    await update({
      ...company,
      levelNames: {
        ...teamLevels,
        [level]: levelName,
      },
    })
  }

  const handleDelete = async (): Promise<void> => {
    if (!company) return
    setIsDeleting(true)
    await update({
      ...company,
      levelNames: {
        ...teamLevels,
        [level]: undefined,
      },
      highestTeamLevel: level === company.highestTeamLevel ? company.highestTeamLevel - 1 : company.highestTeamLevel,
      lowestTeamLevel: level === company.lowestTeamLevel ? company.lowestTeamLevel + 1 : company.lowestTeamLevel,
    }).finally(() => {
      setIsDeleting(false)
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const levelTeams = useMemo(() => allTeams.filter((team) => team.level === level), [allTeams, level])

  const isLevelVisible = useMemo(
    () => levelTeams.every((team) => collapsedTeams.includes(team.teamId)),
    [levelTeams, collapsedTeams]
  )
  const onToggleLevelVisibility = useCallback(() => {
    toggleTeamCollapse(levelTeams.map((team) => team.teamId))
  }, [levelTeams, toggleTeamCollapse])

  const isMinMaxLevel = level === company?.highestTeamLevel || level === company?.lowestTeamLevel

  const showEdit = !withPermissions && canEdit

  if (hideLevelLabels) return

  return (
    <div className='text-md min-w-[100px]'>
      {isEditing ? (
        <Stack gap={Size.space.sm}>
          <TextInput
            maw={150}
            miw={100}
            ref={inputRef}
            value={levelName}
            onChange={(e) => setLevelName(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Group gap={Size.space.md} justify='end' wrap='nowrap'>
            <ConditionTooltip
              conditions={[
                {
                  active: isNotEmpty(levelTeams),
                  label: 'Cannot delete level with teams',
                },
                {
                  active: !isMinMaxLevel,
                  label: 'Cannot only remove top or bottom levels',
                },
              ]}
            >
              <ButtonDelete buttonType='icon-rounded' loading={isDeleting} onClick={handleDelete} />
            </ConditionTooltip>
            <ButtonSave buttonType='icon-rounded' loading={isUpdating && !isDeleting} onClick={handleSave} />
            <ButtonCancel buttonType='icon-rounded' disabled={isUpdating} onClick={handleCancel} />
          </Group>
        </Stack>
      ) : (
        <Stack gap={Size.space.lg}>
          <Text fw={600} size='lg'>
            {levelName}
          </Text>
          <Group gap={Size.space.lg} wrap='nowrap'>
            {showEdit && (
              <>
                <AddTeamButton buttonType='icon-rounded' level={level} variant='gray' />
                <ButtonEdit buttonType='icon-rounded' variant='gray' onClick={handleEdit} />
              </>
            )}
            <ButtonVisibility isHiding={isLevelVisible} onClick={onToggleLevelVisibility} />
          </Group>
        </Stack>
      )}
    </div>
  )
}
